import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, MapPin, Mail, Facebook } from "lucide-react";
import { BUSINESS, LOGO_URL } from "../lib/constants";
import { useLanguage } from "../i18n/LanguageContext";

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
);

const Footer = () => {
    const { t } = useLanguage();

    return (
        <footer
            data-testid="site-footer"
            className="bg-[#3D3D24] text-[#FFFFF3] mt-24"
        >
            <div className="max-w-[1400px] mx-auto px-6 py-16 grid lg:grid-cols-4 md:grid-cols-2 gap-12">
                {/* Brand */}
                <div className="space-y-5 lg:col-span-1">
                    <div className="bg-[#FFFFF3] p-3 inline-flex items-center w-auto">
                        <img
                            src={LOGO_URL}
                            alt="Beauty Ink LV"
                            className="h-20 w-auto object-contain"
                        />
                    </div>
                    <p className="text-sm leading-relaxed opacity-80 max-w-xs">
                        Premium permanent makeup studio in Las Vegas — bilingual, licensed, and obsessed
                        with natural-looking results.
                    </p>
                    <div className="flex items-center gap-3 pt-2">
                        <a
                            data-testid="footer-instagram-1"
                            href={BUSINESS.instagram1}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram Beauty Ink LV"
                            className="w-10 h-10 border border-[#FFFFF3]/30 flex items-center justify-center hover:bg-[#D4A95A] hover:border-[#D4A95A] hover:text-[#3D3D24] transition-colors"
                        >
                            <Instagram size={16} />
                        </a>
                        <a
                            data-testid="footer-tiktok"
                            href={BUSINESS.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="TikTok @beauty.ink.lv"
                            className="w-10 h-10 border border-[#FFFFF3]/30 flex items-center justify-center hover:bg-[#D4A95A] hover:border-[#D4A95A] hover:text-[#3D3D24] transition-colors"
                        >
                            <TikTokIcon size={16} />
                        </a>
                        <a
                            data-testid="footer-facebook"
                            href={BUSINESS.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook Beauty Ink LV"
                            className="w-10 h-10 border border-[#FFFFF3]/30 flex items-center justify-center hover:bg-[#D4A95A] hover:border-[#D4A95A] hover:text-[#3D3D24] transition-colors"
                        >
                            <Facebook size={16} />
                        </a>
                    </div>
                </div>

                {/* Services */}
                <div>
                    <div className="font-label text-xs text-[#D4A95A] mb-5">{t.nav.services}</div>
                    <ul className="space-y-3 text-sm">
                        <li><Link data-testid="footer-link-nano-brows" to="/nano-brows" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.nanoBrows}</Link></li>
                        <li><Link data-testid="footer-link-combo-brows" to="/combo-brows" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.comboBrows}</Link></li>
                        <li><Link data-testid="footer-link-powder-brows" to="/powder-brows" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.powderBrows}</Link></li>
                        <li><Link data-testid="footer-link-lip-blushing" to="/lip-blushing" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.lipBlushing}</Link></li>
                        <li><Link data-testid="footer-link-microblading" to="/microblading" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.microblading}</Link></li>
                        <li><Link data-testid="footer-link-eyeliner-tattoo" to="/eyeliner-tattoo" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.eyelinerTattoo}</Link></li>
                        <li><Link data-testid="footer-link-brow-lamination" to="/brow-lamination" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.browLamination}</Link></li>
                        <li><Link data-testid="footer-link-lashes" to="/lashes" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.lashes}</Link></li>
                        <li><Link data-testid="footer-link-tattoos" to="/tattoos" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.tattoos}</Link></li>
                    </ul>
                </div>

                {/* Studio */}
                <div>
                    <div className="font-label text-xs text-[#D4A95A] mb-5">Studio</div>
                    <ul className="space-y-3 text-sm">
                        <li><Link data-testid="footer-link-aftercare" to="/aftercare" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.aftercare}</Link></li>
                        <li><Link data-testid="footer-link-bed-rentals" to="/bed-rentals" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.bedRentals}</Link></li>
                        <li><Link data-testid="footer-link-training" to="/training" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.training}</Link></li>
                        <li><Link data-testid="footer-link-about" to="/about" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.about}</Link></li>
                        <li><Link data-testid="footer-link-contact" to="/contact" className="opacity-85 hover:opacity-100 hover:text-[#D4A95A]">{t.nav.contact}</Link></li>
                    </ul>
                </div>

                {/* Contact */}
                <div>
                    <div className="font-label text-xs text-[#D4A95A] mb-5">{t.nav.contact}</div>
                    <ul className="space-y-3 text-sm">
                        <li className="flex items-start gap-3 opacity-90">
                            <MapPin size={16} className="mt-0.5 shrink-0 text-[#D4A95A]" />
                            <a
                                data-testid="footer-address"
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.address)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-[#D4A95A] transition-colors"
                            >
                                {BUSINESS.address}
                            </a>
                        </li>
                        <li className="flex items-center gap-3 opacity-90">
                            <Phone size={16} className="text-[#D4A95A]" />
                            <a data-testid="footer-phone" href={`tel:${BUSINESS.phoneRaw}`} className="hover:text-[#D4A95A]">{BUSINESS.phone}</a>
                        </li>
                        <li className="flex items-center gap-3 opacity-90">
                            <Mail size={16} className="text-[#D4A95A]" />
                            <Link to="/contact" className="hover:text-[#D4A95A]">Send a message</Link>
                        </li>
                    </ul>
                    <Link
                        data-testid="footer-book-cta"
                        to="/contact"
                        className="inline-flex mt-6 bg-[#D4A95A] text-[#3D3D24] px-6 py-3 font-label text-xs hover:bg-[#FFFFF3] transition-colors"
                    >
                        {t.common.bookNow}
                    </Link>
                </div>
            </div>

            <div className="border-t border-[#FFFFF3]/15">
                <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs opacity-70">
                    <span>© {new Date().getFullYear()} Beauty Ink LV. All rights reserved.</span>
                    <span className="font-label tracking-[0.2em]">Wendy Chester · PMUA · AE</span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
